import React from "react";

import { QualioQFrontEndProvider } from "@qualio/ui-components";
import type { CurrentUser as QCurrentUser } from "@qualio/ui-components/lib/types/CurrentUser";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { createRoot, Root } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "react-query";

// Store roots for cleanup in MFE architecture
const roots = new Map<string, Root>();

import { App } from "./App";
import { QViewsStylingFix } from "./components/QViewsStylingFix/QViewsStylingFix";
import { setDesignControlsBaseURL } from "./api/apiConfig";
import "./i18n/config";

(window as any).renderDesignControls = async (
  containerId: string,
  currentUser: QCurrentUser,
  analytics: SegmentAnalytics.AnalyticsJS,
) => {
  setDesignControlsBaseURL(import.meta.env.VITE_API_BASE_URL || "");

  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LAUNCHDARKLY_PUBLIC_KEY as string,
    user: {
      key: `${currentUser?.companyId}`,
      custom: {
        loggedInUserId: `${currentUser?.userId}`,
        createdTime: `${currentUser.company?.createdTime}`,
        status: `${currentUser.company?.status}`,
      },
    },
    options: {},
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container #${containerId} not found`);
    return;
  }
  const root = createRoot(container);
  roots.set(containerId, root);
  root.render(
    <QueryClientProvider client={queryClient}>
      <LDProvider>
        <QViewsStylingFix>
          <QualioQFrontEndProvider user={currentUser} analytics={analytics}>
            <App />
          </QualioQFrontEndProvider>
        </QViewsStylingFix>
      </LDProvider>
    </QueryClientProvider>,
  );
};

(window as any).unmountDesignControls = (containerId: string) => {
  try {
    console.log("attempting to unmount container", containerId);
    const root = roots.get(containerId);
    if (root) {
      root.unmount();
      roots.delete(containerId);
    }
  } catch (e) {
    console.error("error unmounting design controls container", e);
  }
};
